import { convertFromPixels } from "client/utils/pixels/convertFromPixels";
import { convertToPixels } from "client/utils/pixels/convertToPixels";
import { roundFloat } from "client/utils/roundFloat";
import { ChangeEvent } from "react";
import { MAX_RESIZE_SIZE_IN_PX } from "store/features/bulkResize/utils/validateResizeDimensions";
import { resizeSetHeight, resizeSetIsLocked, resizeSetUnit, resizeSetWidth } from "store/features/resizeEditor/actions";
import { resizeEditorSelector } from "store/features/resizeEditor/selectors/resizeEditorSelector";
import { ResizeEditorState } from "store/features/resizeEditor/types";
import { useAppDispatch, useAppSelector } from "store/store";
import { Unit } from "store/types/unit";

export const useSize = () => {
    const {
        image,
        resize: {
            byDimension: { height, width },
            isLocked,
            unit,
            dpi,
            dimensionType,
        },
    } = useAppSelector(resizeEditorSelector, equalityFn);

    const widthValid = convertToPixels(width, unit, unit === "pixels" ? undefined : +dpi) <= MAX_RESIZE_SIZE_IN_PX;
    const heightValid = convertToPixels(height, unit, unit === "pixels" ? undefined : +dpi) <= MAX_RESIZE_SIZE_IN_PX;

    const maximumDimension = roundFloat(
        convertFromPixels(MAX_RESIZE_SIZE_IN_PX, unit, unit !== "pixels" ? +dpi : undefined),
    );

    const dispatch = useAppDispatch();

    const handleHeightChange = (e: ChangeEvent<HTMLInputElement>) => dispatch(resizeSetHeight(Number(e.target.value)));

    const handleWidthChange = (e: ChangeEvent<HTMLInputElement>) => dispatch(resizeSetWidth(Number(e.target.value)));

    const toggleLock = () => dispatch(resizeSetIsLocked(!isLocked));

    const handleUnitChange = (unit: Unit) => dispatch(resizeSetUnit(unit));

    return {
        height,
        width,
        handleHeightChange,
        handleWidthChange,
        isLocked,
        toggleLock,
        unit,
        image,
        dpi,
        dimensionType,
        widthValid,
        heightValid,
        maximumDimension,
        handleUnitChange,
    };
};

const equalityFn = (left: ResizeEditorState, right: ResizeEditorState) => {
    if (
        left.resize.byDimension.width !== right.resize.byDimension.width ||
        left.resize.byDimension.height !== right.resize.byDimension.height ||
        left.resize.isLocked !== right.resize.isLocked ||
        left.resize.unit !== right.resize.unit ||
        left.resize.dpi !== right.resize.dpi ||
        left.resize.dimensionType !== right.resize.dimensionType ||
        JSON.stringify(left.image) !== JSON.stringify(right.image)
    )
        return false;

    return true;
};
